'use client';

import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { useState } from 'react';

import { appRoutes } from '@/appRoutes';
import { loginSchema, type LoginSchema } from '@/features/auth/schemas';
import { hardRedirect } from '@/features/auth/utils';
import { MainButton, SmartInput } from '@web/ui';
import { getErrorMessage, notify, zodErrorsToFieldErrors } from '@web/utils';

import { useLoginMutation } from '../api/authApi';
import { useBootstrapAuth } from '../hooks';
import { LoginResponse } from '../types';

import styles from './AuthForm.module.scss';

import type { ItemResponse } from '@web/types';

type LoginFormProps = {
  inviteCode?: string;
  invitedEmail?: string;
};

export function LoginForm({
  inviteCode = '',
  invitedEmail = '',
}: LoginFormProps) {
  const searchParams = useSearchParams();
  const bootstrapAuth = useBootstrapAuth();
  const trimmedInviteCode = inviteCode.trim();
  const trimmedInvitedEmail = invitedEmail.trim();

  const [login, loginStatus] = useLoginMutation();
  const [isRedirecting, setIsRedirecting] = useState(false);

  const [formData, setFormData] = useState<LoginSchema>({
    email: trimmedInvitedEmail,
    password: '',
    remember_me: false, // * For now we won't support this - T-194
    invite_code: trimmedInviteCode || undefined,
  });

  const [errors, setErrors] = useState<
    Partial<Record<keyof LoginSchema, string>>
  >({});

  const isBusy = loginStatus.isLoading || isRedirecting;
  const requestedRedirectPath = searchParams?.get('next');

  const targetRedirectPath =
    requestedRedirectPath &&
    requestedRedirectPath.startsWith('/') &&
    !requestedRedirectPath.startsWith('//')
      ? requestedRedirectPath
      : appRoutes.home;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrors({});

    const parsed = loginSchema.safeParse(formData);

    if (!parsed.success) {
      setErrors(zodErrorsToFieldErrors<LoginSchema>(parsed.error));
      return;
    }

    try {
      const response: ItemResponse<LoginResponse> = await login({
        ...parsed.data,
        ...(trimmedInviteCode ? { invite_code: trimmedInviteCode } : {}),
      }).unwrap();

      notify({
        type: 'success',
        message:
          (response?.message as string) ?? 'Welcome back! Redirecting you now.',
      });

      const { user, workspace, is_workspace_setup_required } =
        response?.data ?? {};

      setIsRedirecting(true);

      bootstrapAuth({
        currentUser: user ?? null,
        currentWorkspaceSlug: workspace?.slug ?? null,
        isWorkspaceSetupRequired: is_workspace_setup_required ?? false,
        isWorkspaceOnboarded: workspace?.is_workspace_onboarded ?? null,
      });

      hardRedirect(targetRedirectPath);
    } catch (error) {
      notify({ type: 'error', message: getErrorMessage(error) });
    }
  };

  return (
    <form
      className={`${styles.form} ${styles.formActive}`}
      onSubmit={handleSubmit}
    >
      <SmartInput
        type="text"
        name="email"
        label={trimmedInvitedEmail ? 'Email from invite' : 'Email'}
        value={formData.email}
        onChange={(value) =>
          setFormData((prev) => ({
            ...prev,
            email: trimmedInvitedEmail ? prev.email : value,
          }))
        }
        schema={loginSchema.shape.email}
        error={errors.email}
        readOnly={Boolean(trimmedInvitedEmail)}
      />

      <SmartInput
        type="password"
        name="password"
        label="Password"
        value={formData.password}
        onChange={(value) =>
          setFormData((prev) => ({ ...prev, password: value }))
        }
        schema={loginSchema.shape.password}
        error={errors.password}
      />

      <div className={styles.formRow}>
        <Link
          href={
            trimmedInviteCode
              ? `${appRoutes.auth.forgotPassword}?invite_code=${encodeURIComponent(
                  trimmedInviteCode,
                )}${
                  trimmedInvitedEmail
                    ? `&email=${encodeURIComponent(trimmedInvitedEmail)}`
                    : ''
                }`
              : appRoutes.auth.forgotPassword
          }
          className="inlineLink"
        >
          Forgot password?
        </Link>
      </div>

      <MainButton
        type="submit"
        disabled={isBusy}
        isLoading={isBusy}
        ariaLabel={isRedirecting ? 'Redirecting...' : 'Log in'}
      >
        {isRedirecting ? 'Redirecting...' : 'Log in'}
      </MainButton>
    </form>
  );
}
